import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { styled } from '../../theming';
import tokens from '@lessonnine/design-tokens.lib/dist/json/variables.json';

import { LogoResponsive } from '../../Logo';
import { NavBarToggle, NavBarToggleProps } from '../NavBarToggle';
import GlobalBodyStyles from './GlobalBodyStyles';
import StyledNavBarWrapper from './StyledNavBarWrapper';
import NavBarGateWrapper from './NavBarGateWrapper';
import NavBarGate from './NavBarGate';
import Nav from './Nav';
import { ResponsiveProp, responsiveProp, responsivePropType } from '../../utils/responsiveProp';
import { LogoWrapper } from './LogoWrapper';
import { Link } from '../../Link';

const logo = <LogoResponsive simplified={{ xxxsmall: true, xsmall: false }} />;

const LeftAlignedSection = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex: 1 1 0;

  > *:nth-child(2) {
    margin-left: ${tokens.space.x8};
  }

  @media (min-width: ${tokens.viewports.breakpoints.xsmall}) {
    flex: 1 1 auto;
  }
`;

const RightAlignedNav = styled(Nav)<{ centerSection?: boolean }>`
  justify-content: flex-end;
  flex: 1 1 auto;
`;

const CenterSection = styled.div<{ fullWidthSection?: ResponsiveProp<boolean> }>`
  flex: 3 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ fullWidthSection }) => ({
    ...responsiveProp(fullWidthSection, (fullWidthSection) => ({
      display: 'block',
      width: fullWidthSection ? '100%' : 'auto',
      flex: fullWidthSection ? 'auto' : '3 1 auto',
    })),
  })}
`;

// ensures center section is aligned centrally when rightAlignedItems not provided
const Spacer = styled.div`
  flex: 1 1 auto;
`;

const MobileMenu = styled(Nav)``;

type CommonProps = {
  banner?: JSX.Element;
  centerSection?: React.ReactNode;
  fullWidthSection?: ResponsiveProp<boolean>;
  logoUrl?: string;
  showLogo?: ResponsiveProp<boolean>;
  onLogoClick?: () => void;
  customLogo?: React.ReactNode;
  isFixed?: boolean;
  leftAlignedItems?: React.ReactNode;
  leftAlignedNavLabel?: string;
  rightAlignedItems?: React.ReactNode;
  rightAlignedNavLabel?: string;
  toggleIdentifier?: NavBarToggleProps['identifier'];
};

// Creating a [union type](https://www.typescriptlang.org/docs/handbook/2/everyday-types.html#union-types), so that
// `toggleLabels` is correctly marked as required only if `children` is defined as well.
type ConditionalProps =
  | { hasMobileMenu?: boolean; toggleLabels: NavBarToggleProps['labels'] }
  | { hasMobileMenu: false; toggleLabels?: never }
  | { hasMobileMenu: true; toggleLabels: NavBarToggleProps['labels'] };

export type NavBarWrapperProps = CommonProps & ConditionalProps;

export const NavBarWrapper = ({
  leftAlignedItems,
  rightAlignedItems,
  centerSection,
  fullWidthSection,
  leftAlignedNavLabel,
  rightAlignedNavLabel,
  hasMobileMenu,
  banner,
  logoUrl,
  showLogo,
  onLogoClick = () => {},
  customLogo,
  isFixed,
  toggleLabels,
  toggleIdentifier,
}: NavBarWrapperProps): JSX.Element => {
  const [isMobileMenuExpanded, setIsMobileMenuExpanded] = useState(false);

  const handleToggleClick = useCallback(() => {
    setIsMobileMenuExpanded(!isMobileMenuExpanded);
  }, [isMobileMenuExpanded, setIsMobileMenuExpanded]);

  const hasItems = leftAlignedItems || rightAlignedItems;
  const displayMenu =
    hasMobileMenu && hasItems && toggleLabels && toggleLabels.open && toggleLabels.close;

  return (
    <>
      <GlobalBodyStyles isMobileMenuExpanded={isMobileMenuExpanded} />
      <StyledNavBarWrapper isMobileMenuExpanded={isMobileMenuExpanded} isFixed={isFixed}>
        {banner}
        <NavBarGateWrapper isMobileMenuExpanded={isMobileMenuExpanded}>
          <NavBarGate alignItems={'center'} padding={[0, 16]}>
            <LeftAlignedSection>
              {customLogo ? (
                customLogo
              ) : (
                <LogoWrapper showLogo={showLogo}>
                  {logoUrl ? (
                    <Link href={logoUrl} id="navbar-babbel-logo" onClick={onLogoClick}>
                      {logo}
                    </Link>
                  ) : (
                    logo
                  )}
                </LogoWrapper>
              )}
              {leftAlignedItems && (
                <Nav
                  aria-label={leftAlignedNavLabel}
                  forwardedAs={'nav'}
                  gap={24}
                  direction="inline"
                  isVisible={{ xxxsmall: !hasMobileMenu, small: true }}
                  alignItems={'center'}
                >
                  {leftAlignedItems}
                </Nav>
              )}
            </LeftAlignedSection>
            {centerSection && (
              <CenterSection fullWidthSection={fullWidthSection}>{centerSection}</CenterSection>
            )}
            {rightAlignedItems ? (
              <RightAlignedNav
                aria-label={rightAlignedNavLabel}
                forwardedAs={'nav'}
                gap={24}
                direction="inline"
                isVisible={{ xxxsmall: !hasMobileMenu, small: true }}
                alignItems={'center'}
                centerSection={centerSection}
              >
                {rightAlignedItems}
              </RightAlignedNav>
            ) : (
              centerSection && <Spacer />
            )}
            {displayMenu && (
              <>
                <NavBarToggle
                  onClick={handleToggleClick}
                  isExpanded={isMobileMenuExpanded}
                  labels={toggleLabels}
                  flex={centerSection ? '1 1 0' : 'initial'}
                  identifier={toggleIdentifier}
                />
                <MobileMenu
                  forwardedAs={'nav'}
                  direction={'stack'}
                  alignItems={'center'}
                  isMobileMenuExpanded={isMobileMenuExpanded}
                  isVisible={{ xxxsmall: isMobileMenuExpanded, small: false }}
                >
                  {leftAlignedItems}
                  {rightAlignedItems}
                </MobileMenu>
              </>
            )}
          </NavBarGate>
        </NavBarGateWrapper>
      </StyledNavBarWrapper>
    </>
  );
};

NavBarWrapper.propTypes = {
  hasMobileMenu: PropTypes.bool,
  showLogo: responsivePropType(PropTypes.bool),
  onLogoClick: PropTypes.func,
  customLogo: PropTypes.node,
};

NavBarWrapper.defaultProps = {
  hasMobileMenu: true,
  showLogo: true,
};
